<template>
  <div>
    <h2 class="text-[13px] font-semibold text-[#23569F] uppercase leading-[1.5]">{{ $t('playerSetup.OutputResolutions') }}</h2>
    <div class="grid">
      <div class="w-1/2 justify-self-center">
        <player-setup-form-error :message="externalError" :display="externalError != null && externalError != ''" height="minimal" />
      </div>
    </div>

    <div v-if="isVVArrangement">
      <el-row class="page-led-adjustments flex  mt-[20px]">
        <el-col :span="12">
          <div class="ml-12">
            <table class="border-l border-t border-[#B9B9B9] w-full text-black text-sm">
              <tbody>
                <tr class="bg-[#F0F0F0]">
                  <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                    <div class="pb-[15px]">Output</div>
                  </th>
                  <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                    <div class="leading-[1.25rem]">Width (px)</div>
                    <div :class="[{ 'has-error': validationFailed.width }, 'text-[#909090] text-[10px] font-normal leading-[1.25rem]']">max. {{ maxWidth }}</div>
                  </th>
                  <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                    <div class="leading-[1.25rem]">Height (px)</div>
                    <div :class="[{ 'has-error': validationFailed.height }, 'text-[#909090] text-[10px] font-normal leading-[1.25rem]']">max. {{ maxHeight }}</div>
                  </th>
                </tr>
                <tr v-for="(item) in sortedTableData" :key="'table-' + item.label">
                  <td class="bg-[#F0F0F0] border-r border-b border-[#B9B9B9] font-bold px-3 py-[10px] w-1/3 leading-[1.25rem]">{{ item.label }}</td>
                  <td class="border-r border-b border-[#B9B9B9] relative p-0 w-1/3">
                    <number-masked-input
                      :class="[{ 'has-error': validationFailed.outputs[item.index]?.width }, 'border-none px-3 py-[10px] w-full outline-hidden absolute top-0 h-full']"
                      :model-value="item.width"
                      @update:model-value="changeResolution('width', item.index, $event)"
                      :locale="locale"
                    />
                  </td>
                  <td class="border-r border-b border-[#B9B9B9] relative p-0 w-1/3">
                    <number-masked-input
                      :class="[{ 'has-error': validationFailed.outputs[item.index]?.height }, 'border-none px-3 py-[10px] w-full outline-hidden absolute top-0 h-full']"
                      :model-value="item.height"
                      @update:model-value="changeResolution('height', item.index, $event)"
                      :locale="locale"
                    />
                  </td>
                </tr>
             </tbody>
            </table>
          </div>

          <div class="ml-12 pt-8">
            <player-setup-canvas
              canvasType="led"
              :width="totalResolution.width"
              :height="totalResolution.height"
              @updateCanvasInfo="updateCanvasInfo"
              @canvasCheckInProgress="toggleCanvasCheckProgress"
            />
          </div>
        </el-col>
        <el-col :span="12" class="flex! justify-center items-center">
          <div :class="[gridProperty, 'grid gap-0']">
            <div
              v-for="item in tableData"
              :key="'output-' + item.label"
              :class="[outputBoxProperty, 'border-black border grid text-xs']">
              <img
                :class="`preview-${displayOrientation} justify-self-center self-end`"
                :alt="displayOrientation">
              <div class="self-center justify-self-center"> {{ item.label }} </div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <div v-else>
      <el-row class="page-led-adjustments flex pb-8  mt-[20px]">
        <el-col :span="10">
          <div class="ml-12">
            <table class="border-l border-t border-[#B9B9B9] w-full text-black text-sm">
              <tbody>
              <tr class="bg-[#F0F0F0]">
                <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                  <div class="pb-[15px]">Output</div>
                </th>
                <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                  <div class="leading-[1.25rem]">Width (px)</div>
                  <div :class="[{ 'has-error': validationFailed.width }, 'text-[#909090] text-[10px] font-normal leading-[1.25rem]']">max. {{ maxWidth }}</div>
                </th>
                <th class="border-r border-b border-[#B9B9B9] px-3 py-[10px]">
                  <div class="leading-[1.25rem]">Height (px)</div>
                  <div :class="[{ 'has-error': validationFailed.height }, 'text-[#909090] text-[10px] font-normal leading-[1.25rem]']">max. {{ maxHeight }}</div>
                </th>
              </tr>
              <tr v-for="(item) in sortedTableData" :key="'table-' + item.label">
                <td class="bg-[#F0F0F0] border-r border-b border-[#B9B9B9] font-bold px-3 py-[10px] w-1/3 leading-[1.25rem]">{{ item.label }}</td>
                <td class="border-r border-b border-[#B9B9B9] relative p-0 w-1/3">
                  <number-masked-input
                      :class="[{ 'has-error': validationFailed.outputs[item.index]?.width }, 'border-none px-3 py-[10px] w-full outline-hidden absolute top-0 h-full']"
                      :model-value="item.width"
                      @update:model-value="changeResolution('width', item.index, $event)"
                      :locale="locale"
                    />
                </td>
                <td class="border-r border-b border-[#B9B9B9] relative p-0 w-1/3">
                  <number-masked-input
                      :class="[{ 'has-error': validationFailed.outputs[item.index]?.height }, 'border-none px-3 py-[10px] w-full outline-hidden absolute top-0 h-full']"
                      :model-value="item.height"
                      @update:model-value="changeResolution('height', item.index, $event)"
                      :locale="locale"
                    />
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        </el-col>
        <el-col :span="14" class="flex! justify-center items-center">
          <div :class="[gridProperty, 'grid gap-0']">
            <div
              v-for="item in tableData"
              :key="'output-' + item.label"
              :class="[outputBoxProperty, 'border-black border grid text-xs']">
              <img
                :class="`preview-${displayOrientation} justify-self-center self-end`"
                :alt="displayOrientation">
              <div class="self-center justify-self-center"> {{ item.label }} </div>
            </div>
          </div>
        </el-col>
      </el-row>

      <el-row>
        <el-col class="ml-12" :span="12">
          <player-setup-canvas
            canvasType="led"
            :width="totalResolution.width"
            :height="totalResolution.height"
            @updateCanvasInfo="updateCanvasInfo"
            @canvasCheckInProgress="toggleCanvasCheckProgress"
          />
        </el-col>
      </el-row>
    </div>
  </div>
</template>
<script src="./player-setup-led.js"></script>
<style src="./player-setup-led.scss" lang="scss"></style>
